import styles from './modal.module.css'
import { useUIState, useUIStateActions } from '../../hooks/uiHooks'
import { ModalId } from './types'
import { useUIActions } from '../../actions/uiActions'
import { ActionButton } from '../action-button/actionButton'
import { Action } from '../types'
import { useEffect } from 'react'
import { Button } from '../button/button'
import { ButtonKind } from '../button/types'

type Props = {
    modalId: ModalId
    title: string
    children: JSX.Element | JSX.Element[]
    submitLabel?: string
    submitIcon?: string
    submitKind?: ButtonKind
    disableSubmit?: boolean
    cancelLabel?: string
    closeable?: boolean
    widthSize?: 's' | 'm' | 'l'
    actions?: Action[]
    hideCancel?: boolean
    focusSubmit?: boolean
    secondaryLabel?: string
    secondaryIcon?: string
    secondaryKind?: ButtonKind
    disableSecondary?: boolean
    onSubmit?: () => void | Promise<void>
    onCancel?: () => void
    onSecondarySubmit?: () => void
}

export const Modal = (props: Props) => {
    const { modalId, title, children, submitLabel, submitIcon, submitKind, disableSubmit, cancelLabel, closeable = true, widthSize = 's', actions, hideCancel, focusSubmit, secondaryLabel, secondaryIcon, secondaryKind, disableSecondary, onSubmit, onCancel, onSecondarySubmit } = props
    const { openedModal } = useUIState()
    const { setOpenedModal } = useUIStateActions()
    const { closeModal, closeModalOverlay } = useUIActions()

    useEffect(() => {
        return () => {
            closeModalOverlay()
        }
    }, [])

    const showFooter = !actions || actions.length > 0

    const containerClassName = `${styles.container} ${widthSize === 's' ? styles.s : (widthSize === 'm' ? styles.m : styles.l)}`

    const onClose = () => openedModal && setOpenedModal(undefined)

    const handleCancel = () => {
        closeModal(modalId)
        onCancel?.()
    }

    const handleSubmit = async () => {
        try {
            await onSubmit?.()
        } finally {
            closeModal(modalId)
        }
    }
    
    const onModalClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation()

    const renderActionBtn = (action: Action, index: number) => {
        return <ActionButton
            key={index}
            action={action}
            onClick={action.onClick}
        />
    }

    const renderSubmitBtn = () => {
        return <Button
            label={submitLabel || 'Confirmar'}
            kind={submitKind || 'primary'}
            icon={submitIcon}
            disabled={disableSubmit}
            focus={focusSubmit}
            onClick={handleSubmit}
        />
    }

    const renderSecondarySubmitBtn = () => {
        return <Button
            label={secondaryLabel || 'Acción secundaria'}
            kind={secondaryKind || 'quinary'}
            icon={secondaryIcon}
            disabled={disableSecondary}
            onClick={onSecondarySubmit}
        />
    }

    const renderCancelBtn = () => {
        return <Button
            label={cancelLabel || 'Cancelar'}
            kind='tertiary'
            onClick={handleCancel}
        />
    }

    return (
        <div className="modal fade" id={modalId} onClick={onClose} tabIndex={-1}>
            {!closeable && <div className={styles.overlay} />}
            <div className={`modal-dialog modal-dialog-centered ${containerClassName}`} onClick={onModalClick}>
                <div className={`modal-content ${styles.content}`}>
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        {closeable && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            />
                        )}
                    </div>
                    <div className={`modal-body ${styles.body}`}>
                        {children}
                    </div>
                    {showFooter && (
                        <div className="modal-footer">
                            {actions ? actions.map(renderActionBtn) : (
                                <>
                                    {onSubmit && renderSubmitBtn()}
                                    {onSecondarySubmit && renderSecondarySubmitBtn()}
                                    {!hideCancel && renderCancelBtn()}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <button
                id={`open-${modalId}`}
                data-bs-toggle="modal"
                data-bs-target={`#${modalId}`}
                className={styles.openerBtn}
            />
        </div>
    )
}
